import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Text, Button, Checkbox, Flex, rem } from '@mantine/core';
import { MouseEvent, useState } from 'react';
import { PhoneNumberInput } from '@dealstack/frontend/shared/components/forms/PhoneNumberInput/PhoneNumberInput';
import { INVESTOR_PHONE_FORM } from '@dealstack/frontend/shared/pages/InvestorPhone/constants';
import {
	useTranslationAuth,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import styled from 'styled-components';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { normalizePhoneNumberForBE } from '@dealstack/shared/lib/format/string';

export interface InvestorPhoneSubmission {
	telephonePrefix: string;
	phoneNumber: string;
}

interface Props {
	onSubmit: (submission: InvestorPhoneSubmission) => void;
	isSubmitting: boolean;
	setIsTermsModalOpened: (open: boolean) => void;
	setIsPrivacyModalOpened: (open: boolean) => void;
}

export const InvestorPhoneForm = ({
	onSubmit,
	isSubmitting,
	setIsTermsModalOpened,
	setIsPrivacyModalOpened,
}: Props) => {
	const tAuth = useTranslationAuth();
	const tInvestor = useTranslationInvestor();

	const commonYup = useCommonYupValidation();
	const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

	const validationSchema = Yup.object().shape({
		[INVESTOR_PHONE_FORM.PHONE_NUMBER]: commonYup.phone.required,
	});

	const phoneForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema),
	});

	const submit = (values: FieldValues) => {
		const telephonePrefix = `${values[INVESTOR_PHONE_FORM.TELEPHONE_PREFIX] ?? ''}`;
		const fullNumber = `${values[INVESTOR_PHONE_FORM.PHONE_NUMBER] ?? ''}`;

		onSubmit({
			telephonePrefix,
			phoneNumber: normalizePhoneNumberForBE(fullNumber, telephonePrefix),
		});
	};

	return (
		<>
			<Flex direction="column" align="center">
				<HeaderLogo size="sm" variant="dealstack-logo-black" className="mb-[24px]" />
				<Text size="B3Bold" c="dark.8" className="mb-[12px]">
					{tAuth('register.main_title')}
				</Text>
			</Flex>
			<FormProvider {...phoneForm}>
				<form
					onSubmit={phoneForm.handleSubmit(submit)}
					className="flex flex-col gap-6 w-full"
				>
					<StyledPhoneNumberInput
						name={INVESTOR_PHONE_FORM.PHONE_NUMBER}
						phonePrefixName={INVESTOR_PHONE_FORM.TELEPHONE_PREFIX}
						label={tAuth('common-form.phoneNumber.title')}
						size="m"
					/>
					<Checkbox
						checked={hasAcceptedTerms}
						onChange={(event) => setHasAcceptedTerms(event.currentTarget.checked)}
						data-testid={INVESTOR_TEST_ELEMENT_ID.ACCEPT_INVITATION_TERMS_CHECKBOX}
						label={
							<Text size="B6Medium" c="dark.0">
								{tInvestor('invite.read_accept')}
								<span
									onClick={(event) =>
										openWithoutToggling(event, setIsTermsModalOpened)
									}
									className="text-[#0D5EF2] cursor-pointer"
								>
									{tInvestor('invite.terms')}
								</span>
								{tInvestor('invite.and')}
								<span
									onClick={(event) =>
										openWithoutToggling(event, setIsPrivacyModalOpened)
									}
									className="text-[#0D5EF2] cursor-pointer"
								>
									{tInvestor('invite.privacy')}
								</span>
							</Text>
						}
					/>
					<Button
						type="submit"
						disabled={!phoneForm.formState.isValid || !hasAcceptedTerms || isSubmitting}
						variant="primary"
						h="3rem"
						data-testid={INVESTOR_TEST_ELEMENT_ID.ACCEPT_INVITATION_SIGNUP_BUTTON}
					>
						{tAuth('register.button_text')}
					</Button>
				</form>
			</FormProvider>
		</>
	);
};

const openWithoutToggling = (event: MouseEvent, open: (open: boolean) => void) => {
	event.preventDefault();
	open(true);
};

const StyledPhoneNumberInput = styled(PhoneNumberInput)`
	div:first-child > span {
		font-size: ${rem(14)};
		line-height: ${rem(20)};
		font-weight: 600;
	}
`;

const HeaderLogo = styled(Icon)`
	&.svg {
		width: 6rem;
		height: unset;
	}
`;
